import React, { useEffect } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react'
import { Controller, useForm } from 'react-hook-form'
import { EmailTemplate } from '../../../../types/emailTemplates'
import { EmailItem } from '../emailsData'
import useEmailTemplate from './useEmailTemplate'

interface EmailsContentProps {
  email: EmailItem
}

const EmailsContent: React.FC<EmailsContentProps> = ({ email }) => {
  const toast = useToast()
  const { template, isLoading, isSaving, save } = useEmailTemplate(email.key)
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EmailTemplate>({ defaultValues: template })

  useEffect(() => {
    if (!isLoading) reset(template)
  }, [isLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: EmailTemplate) => {
    try {
      await save(values)
      toast({ title: 'Saved', status: 'success', duration: 3000, isClosable: true })
    } catch {
      toast({
        title: 'Error saving template',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  return (
    <Box w='100%' p={4} h='100%' overflowY='auto'>
      <Box borderWidth='1px' borderRadius='lg' p={4} mb={4}>
        <Heading size='md'>{email.name}</Heading>
        <Text mt={2}>{email.description}</Text>
      </Box>
      <Box as='form' onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={4}>
          <Controller
            name='subject'
            control={control}
            rules={{ required: 'Subject is required' }}
            render={({ field }) => (
              <FormControl isInvalid={!!errors.subject}>
                <FormLabel>Subject</FormLabel>
                <Input {...field} isDisabled={isLoading} />
                <Text fontSize='xs' color='gray.500' mt={1}>
                  Variables: {'{monthName}'}, {'{year}'}
                </Text>
              </FormControl>
            )}
          />
          <Controller
            name='body'
            control={control}
            rules={{ required: 'Body is required' }}
            render={({ field }) => (
              <FormControl isInvalid={!!errors.body}>
                <FormLabel>Body</FormLabel>
                <Textarea {...field} rows={5} isDisabled={isLoading} />
                <Text fontSize='xs' color='gray.500' mt={1}>
                  The greeting line &quot;&#123;firstName&#125;,&quot; is added automatically above
                  this text.
                </Text>
              </FormControl>
            )}
          />
          <Button
            type='submit'
            colorScheme='purple'
            isLoading={isSaving}
            isDisabled={isLoading}
            alignSelf='flex-start'
          >
            Save
          </Button>
        </Stack>
      </Box>
    </Box>
  )
}

export default EmailsContent
